"use client";

import { useEffect, useRef, useState } from "react";
import { InsuraBotIcon } from "@/app/_module/components/icons/InsuraBotIcon";
import {
  beginThreadSummarySessions,
  summarizeThread,
  threadSummaryAvailability,
  SummaryEngine,
  SummaryProgress,
  ThreadSummaryResult,
} from "@/app/_module/services/localThreadSummary";

// "Summarize with InsuraBot" — on-device (Chrome's built-in model), so it's
// free and the thread never leaves the reader's browser. Renders on both
// surfaces; `surface` picks the class set (public site vs CRM tokens).
export function ThreadSummary({
  surface,
  title,
  body,
  replies,
}: {
  surface: "public" | "crm";
  title: string;
  body: string;
  replies: Array<{ author: string; text: string }>;
}) {
  const [availability, setAvailability] = useState<"checking" | "ready" | "download" | "unavailable">("checking");
  const [engine, setEngine] = useState<SummaryEngine | null>(null);
  const [progress, setProgress] = useState<SummaryProgress | null>(null);
  const [result, setResult] = useState<ThreadSummaryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    threadSummaryAvailability().then(({ engine, state }) => {
      if (cancelled) return;
      setEngine(engine);
      setAvailability(!engine ? "unavailable" : state === "available" ? "ready" : "download");
    });
    return () => {
      cancelled = true;
      abort.current?.abort();
    };
  }, []);

  const run = async () => {
    if (busy || !engine) return;
    abort.current = new AbortController();
    // FIRST statement, no await before it: the model download needs the click.
    const sessions = beginThreadSummarySessions(engine, setProgress, abort.current.signal);
    setBusy(true);
    setError(null);
    setResult(null);
    setProgress({ stage: availability === "download" ? "downloading" : "running", pct: availability === "download" ? 0 : undefined });
    try {
      const r = await summarizeThread({ title, body, replies }, sessions, setProgress, abort.current.signal);
      setResult(r);
      setAvailability("ready");
    } catch (e: any) {
      setError(e?.message === "unavailable" ? "unavailable" : "InsuraBot couldn't summarize this thread. Try again.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const pub = surface === "public";
  const shell = pub ? "rounded-lg border border-slate-200 bg-white" : "rounded-lg border bg-card";
  const eyebrow = pub ? "fp-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500" : "font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground";
  const muted = pub ? "text-slate-500" : "text-muted-foreground";
  const ink = pub ? "text-slate-900" : "text-foreground";
  const btn = pub
    ? "inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 text-[13px] font-semibold text-slate-800 transition-colors hover:border-blue-400 hover:text-blue-700 disabled:opacity-60"
    : "inline-flex h-8 items-center gap-2 rounded-md border bg-card px-3 text-[12px] font-semibold text-foreground transition-colors hover:border-primary/50 hover:text-primary disabled:opacity-60";

  const replyCount = replies.length;
  const label = busy
    ? progress?.stage === "downloading"
      ? `Getting InsuraBot ready${typeof progress.pct === "number" ? ` · ${progress.pct}%` : "…"}`
      : progress?.stage === "checking"
        ? "Checking…"
        : "Reading the thread…"
    : "Summarize with InsuraBot";

  return (
    <div className={`${shell} px-4 py-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className={eyebrow}>InsuraBot</p>
          <p className={`mt-0.5 text-[12px] ${muted}`}>
            {replyCount > 0 ? `The post and ${replyCount} ${replyCount === 1 ? "reply" : "replies"}, summarized.` : "The post, summarized."}
          </p>
        </div>
        <button type="button" onClick={run} disabled={busy || availability === "checking" || availability === "unavailable"} className={btn}>
          <InsuraBotIcon size={16} className="shrink-0" />
          {label}
        </button>
      </div>

      {availability === "unavailable" && (
        <p className={`mt-2 text-[12px] ${muted}`}>
          InsuraBot summaries are available in Chrome on desktop. Open this thread there to use it.
        </p>
      )}
      {error && error !== "unavailable" && (
        <p className="mt-2 text-[12px] text-red-600" role="alert">
          {error}
        </p>
      )}
      {result && (
        <div className="mt-3">
          <p className={`text-[15px] leading-[1.6] ${ink}`}>{result.summary}</p>
          {result.takeaways.length > 0 && (
            <ul className={`mt-2 space-y-1 text-[14px] leading-6 ${ink}`}>
              {result.takeaways.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className={`shrink-0 ${pub ? "text-blue-600" : "text-primary"}`}>·</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          )}
          <p className={`mt-2 text-[11px] ${muted}`}>Generated from this thread only. Check numbers against the replies before you use them.</p>
        </div>
      )}
    </div>
  );
}
